import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import Link from react-router-dom
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Card,
  CardHeader,
  CardContent,
  Chip,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { styled } from "@mui/material/styles";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon    from '@mui/icons-material/Visibility'
import OpenInFullIcon   from '@mui/icons-material/OpenInFull'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import ClientName from "../Clients/FormComponents/Edit/ClientName";
import ClientAddress from "./FormComponents/Edit/ClientAddress";
import ClientContact from "./FormComponents/Edit/ClientContact";
import ClientLogs from "./ClientLogs";
import { clientValidationSchema } from "./utils/clientValidationSchema";

import { getNationalAddresses } from "../API/nationalAddressAPI";
import {
  getClient,
  getCacheClients,
  putClient,
  putCacheClient,
} from "../API/clientAPI";
import { 
  postClientNotes,
  getClientNotesById
} from "../API/clientNotesAPI";
import { postClientLogs } from "../API/clientLogsAPI";
import { getUsersByRoles } from "../../components/API/userAPI";
import { customAlphabet } from "nanoid";


import ContractsTable from "../Billing/components/ContractsTable";
import InvoicesTable from "../Billing/components/InvoicesTable";

import BaseWrapper from "../../components/shared/BaseWrapper";
import { set } from "date-fns";

import { getContractItem, getInvoicesByClientId } from "../API/billingAPI";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function ManageClients() {
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [originalClient, setOriginalClient] = useState(null); // Track original client data for logging
  const [clientsCache, setClientsCache] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [selectedClient, setSelectedClient] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isFetchingClient, setIsFetchingClient] = useState(false);
  const [hasNameChanged, setHasNameChanged] = useState(false); // New state variable
  const [selectedParentClient, setSelectedParentClient] = useState(null);
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const generateId = customAlphabet(alphabet, 8); // Creates a function to generate an 8-character ID
  const [errors, setErrors] = useState({});
  const [clientNotes, setClientNotes] = useState([]);
  const [hideHistory, setHideHistory] = useState(false)
  const [dialogOpen, setDialogOpen]   = useState(false)
  const [nationalAddresses, setNationalAddresses] = useState([]);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const stored = localStorage.getItem("user");
  const userUpdate = stored ? JSON.parse(stored).username : "";

  // Deep copy function that preserves Date objects
  const deepCopyClient = (obj) => {
    if (!obj) return null;
    
    const copy = { ...obj };
    
    // Preserve Date objects
    if (obj.contract_start instanceof Date) {
      copy.contract_start = new Date(obj.contract_start.getTime());
    }
    if (obj.contract_end instanceof Date) {
      copy.contract_end = new Date(obj.contract_end.getTime());
    }
    
    // Deep copy arrays
    if (Array.isArray(obj.service_types)) {
      copy.service_types = [...obj.service_types];
    }
    
    // Deep copy parent_client object
    if (obj.parent_client && typeof obj.parent_client === 'object') {
      copy.parent_client = { ...obj.parent_client };
    }
    
    return copy;
  };

  const competencies = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection",
  ];

  const locationTypeOptions = React.useMemo(() => [
    "Bank",
    "Bar",
    "BPO",
    "Commercial Complex",
    "Commissary",
    "Condo Unit",
    "Condominium",
    "Department Store",
    "Embassy",
    "Food Manufacturing",
    "Government Office",
    "Hospital or Clinic",
    "Hotel",
    "Industrial Plant",
    "Mall",
    "Manufacturing",
    "Office",
    "Parks and Parking",
    "Residential (Apartment)",
    "Residential (Single Unit)",
    "Restaurant or Café",
    "School",
    "Sports Center",
    "Supermarket",
    "Vehicle",
    "Village",
    "Warehouse",
    "Wet Market",
    "Other"
  ], []);

  const allLocationTypes = React.useMemo(() => {
    // if the current value isn’t in our new list, prepend it so it shows up
    if (
      client?.location_type &&
      !locationTypeOptions.includes(client.location_type)
    ) {
      return [client.location_type, ...locationTypeOptions];
    }
    return locationTypeOptions;
  }, [client?.location_type, locationTypeOptions]);  

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const clientsCache = await getCacheClients();
        setClientsCache(clientsCache);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCacheClients();
  }, []);

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });

  // Load national addresses once
  useEffect(() => {
    const loadNational = async () => {
      try {
        const data = await getNationalAddresses();
        setNationalAddresses(data);
      } catch (err) {
        console.error("Failed to load national addresses:", err);
      }
    };
    loadNational();
  }, []);

  const handleSearchClient = (event, value) => {
    setIsFormVisible(false);
    setSelectedClient(value || null);
  };

  const handleSearchButtonClick = async () => {
    if (!selectedClient) {
      console.log("No client selected");
      return;
    }

    if (isFetchingClient) {
      console.log("Already fetching client details...");
      return;
    }

    setIsFetchingClient(true);
    setIsLoading(true);

    try {
      const fetchedClient = await getClient(selectedClient.id);

      fetchedClient.addresses = (fetchedClient.addresses || []).map(addr => ({
        ...addr,
      }));

      // parse ISO strings into Date objects
      fetchedClient.contract_start = fetchedClient.contract_start ? new Date(fetchedClient.contract_start) : null;
      fetchedClient.contract_end = fetchedClient.contract_end ? new Date(fetchedClient.contract_end) : null;
      fetchedClient.account_owner = normalizeOwner(fetchedClient.account_owner);

      // Normalize booleans (older records may have null/undefined or string values)
      const _bool = (v) => v === true || v === "true";
      fetchedClient.regular = _bool(fetchedClient.regular); // default false
      fetchedClient.work_permit_required = _bool(fetchedClient.work_permit_required); // default false

      // fetch existing notes
      const notes = await getClientNotesById(selectedClient.id);

      setClientNotes(notes);
      // clear the input field but keep notesHistory separate
      const clientData = {
        ...fetchedClient,
        notes: "",
      };
      setClient(clientData);
      setOriginalClient(deepCopyClient(clientData)); // Deep copy for comparison
      setSelectedParentClient(fetchedClient.parent_client);
      setIsFormVisible(true);
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setIsFetchingClient(false);
      setIsLoading(false);
    }

    setContractsLoading(true);
    try {
      const contractData = await getContractItem(selectedClient.id);
      setContracts(contractData?.contracts ?? []);
    } catch { setContracts([]); }
    finally { setContractsLoading(false); }

    setInvoicesLoading(true);
    try {
      const invoiceData = await getInvoicesByClientId(selectedClient.id);
      setInvoices(invoiceData?.items ?? []);
    } catch { setInvoices([]); }
    finally { setInvoicesLoading(false); }
  };

  const handleViewEditButtonClick = () => {
    setIsEdit((prevIsEdit) => {
      // If currently in edit mode and about to lock the record, clean up spaces
      if (prevIsEdit) {
        // Clean all text fields when locking the record
        setClient((prevClient) => {
          if (!prevClient) return prevClient;
          
          const cleanedClient = {
            ...prevClient,
            name: cleanSpaces(prevClient.name),
            addresses: prevClient.addresses?.map(addr => ({
              ...addr,
              area: cleanSpaces(addr.area),
              house_unit_street: cleanSpaces(addr.house_unit_street),
              barangay: cleanSpaces(addr.barangay),
              city: cleanSpaces(addr.city),
              metro_province: cleanSpaces(addr.metro_province),
              zipcode: cleanSpaces(addr.zipcode),
            })) || [],
            contacts: prevClient.contacts?.map(contact => ({
              ...contact,
              firstname: cleanSpaces(contact.firstname),
              lastname: cleanSpaces(contact.lastname),
              numbers: contact.numbers?.map(num => ({
                ...num,
                number: cleanSpaces(num.number),
              })) || [],
              emails: contact.emails?.map(email => ({
                ...email,
                email: cleanSpaces(email.email),
              })) || [],
            })) || [],
            account_owner: normalizeOwner(prevClient.account_owner),
            price: cleanSpaces(prevClient.price),
            payment_terms: cleanSpaces(prevClient.payment_terms),
            notes: cleanSpaces(prevClient.notes),
          };
          
          return cleanedClient;
        });
      }
      
      return !prevIsEdit;
    });
  };

  const handleClientNameChange = (value) => {
    setClient((prev) => ({
      ...prev,
      name: value,
    }));

    setHasNameChanged(true);
  };

  const handleAddressChange = (index, field, value) => {
    setClient((prev) => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) =>
        i === index ? { ...addr, [field]: value } : addr
      ),
    }));
  };

  const handleBarangayChange = (index, brgy) => {
    handleAddressChange(index, "barangay", brgy || "");
  };

  const handleCityChange = (index, city) => {
    handleAddressChange(index, "city", city || "");
  };

  // Handler for changing the checkbox (only one can be selected)
  const handleAddressCheckboxChange = (index) => {
    setClient((prev) => ({
      ...prev,
      addresses: prev.addresses.map((address, i) => ({
        ...address,
        billing: i === index, // Set billing_address to true only for the selected index
      })),
    }));
  };

  // Handler to add a new address row
  const handleAddAddress = () => {
    setClient(prev => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          id: generateId(),
          house_unit_street: "",
          metro_province: "",  // ← new
          city: "",
          barangay: "",
          zipcode: "",
          area: "",
          billing: false
        }
      ]
    }));
  };

  // Handler to remove an address row
  const handleRemoveAddress = (index) => {
    setClient((prev) => {
      const updatedAddresses = prev.addresses.filter(
        (_, addrIndex) => addrIndex !== index
      );

      // If only one address remains, set its billing field to true
      if (updatedAddresses.length === 1) {
        updatedAddresses[0] = { ...updatedAddresses[0], billing: true };
      }

      return {
        ...prev,
        addresses: updatedAddresses,
      };
    });
  };

  // Handler to update contact fields
  const handleContactChange = (index, field, value) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ),
    }));
  };

  const handleAddContact = () => {
    setClient((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          id: generateId(),
          firstname: "",
          lastname: "",
          billing: false,
          birthday: "",
          numbers: [
            {
              id: generateId(),
              type: "mobile",
              number: "",
            },
          ],
          emails: [
            {
              id: generateId(),
              email: "",
            },
          ],
        },
      ],
    }));
  };

  // Handler to remove a contact person
  const handleRemoveContact = (index) => {
    setClient((prev) => {
      const updatedContacts = prev.contacts.filter(
        (_, contactIndex) => contactIndex !== index
      );

      // If only one address remains, set its billing field to true
      if (updatedContacts.length === 1) {
        updatedContacts[0] = { ...updatedContacts[0], billing: true };
      }

      return {
        ...prev,
        contacts: updatedContacts,
      };
    });
  };

  const handleContactCheckboxChange = (index) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) => ({
        ...contact,
        billing: i === index, // Set billing_address to true only for the selected index
      })),
    }));
  };

  const handleContactDetailsChange = (
    contactIndex,
    field,
    subIndex,
    subField,
    value
  ) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            [field]: contact[field].map((item, j) =>
              j === subIndex ? { ...item, [subField]: value } : item
            ),
          }
          : contact
      ),
    }));
  };

  const handleAddNumber = (contactIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            numbers: [
              ...contact.numbers,
              { id: generateId(), type: "mobile", number: "" },
            ],
          }
          : contact
      ),
    }));
  };

  const handleRemoveNumber = (contactIndex, numberIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            numbers:
              contact.numbers.length > 1
                ? contact.numbers.filter((_, j) => j !== numberIndex)
                : contact.numbers, // Keep at least one number if only one remains
          }
          : contact
      ),
    }));
  };

  const handleAddEmail = (contactIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            emails: [...contact.emails, { id: generateId(), email: "" }],
          }
          : contact
      ),
    }));
  };

  const handleRemoveEmail = (contactIndex, emailIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            emails:
              contact.emails.length > 1
                ? contact.emails.filter((_, j) => j !== emailIndex)
                : contact.emails, // Keep at least one number if only one remains
          }
          : contact
      ),
    }));
  };

  const handleCategoryChange = (value) => {
    setClient((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleSelectedParentClient = (event, value) => {
    setSelectedParentClient(value || null); // Update the selected client
    setClient((prev) => ({
      ...prev,
      parent_client: value,
    }));
  };

  const handleServiceTypeChange = (e, formattedOption) => {
    setClient((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedServiceTypes = isChecked
        ? [...prev.service_types, formattedOption] // Add formattedOption if checked
        : prev.service_types.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedServiceTypes === prev.service_types) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        service_types: updatedServiceTypes,
      };
    });
  };

  const handleLocationTypeChange = (value) => {
    setClient((prev) => ({
      ...prev,
      location_type: value,
    }));
  };

  const handlePriceChange = (value) => {
    setClient((prev) => ({
      ...prev,
      price: value,
    }));
  };

  const handlePaymentTermsChange = (value) => {
    setClient((prev) => ({
      ...prev,
      payment_terms: value,
    }));
  };

  // Date change handlers
  const handleContractStartChange = (date) => {
    setClient(prev => {
      let end = prev.contract_end;
      if (date) {
        if (!prev.contract_end || prev.contract_end <= date) {
          const tmp = new Date(date);
          tmp.setFullYear(tmp.getFullYear() + 1);
          end = tmp;
        }
      } else {
        end = null;
      }
      return { ...prev, contract_start: date, contract_end: end };
    });
  };

  const handleContractEndChange = (date) => {
    setClient(prev => ({ ...prev, contract_end: date }));
  };

  const handleAccountOwnerChange = (value) => {
    setClient(prev => ({ ...prev, account_owner: value }));
  };

  const handleWorkPermitRequiredChange = (value) => {
    // value comes as string from RadioGroup; normalize to boolean
    const boolVal = value === "true";
    setClient((prev) => ({
      ...prev,
      work_permit_required: boolVal,
    }));
  };
  
  const handleRegularChange = (event) => {
    setClient((prev) => ({
      ...prev,
      regular: event.target.value === "true",
    }));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setIsLoadingOwners(true);
        const res = await getUsersByRoles("customer_service", { match: "any", limit: 200 });
        const list = Array.isArray(res) ? res : (res?.users || res?.items || []);
        const options = (list || [])
          .map(u => ({
            id: u.id ?? u.user_id ?? u.username ?? "",
            firstname: u.firstname ?? "",
            lastname: u.lastname ?? "",
          }))
          .filter(o => o.id && (o.firstname || o.lastname))
          .sort((a, b) =>
            `${a.lastname}, ${a.firstname}`.localeCompare(`${b.lastname}, ${b.firstname}`)
          );
        if (alive) setAccountOwnerOptions(options);
      } catch (err) {
        console.error("Failed to fetch account owners:", err);
        if (alive) setAccountOwnerOptions([]);
      } finally {
        if (alive) setIsLoadingOwners(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Normalize account_owner from object/string/null → { id, firstname, lastname }
  const normalizeOwner = (v) => {
    if (v && typeof v === "object") {
      return {
        id: (v.id ?? "").toString().trim(),
        firstname: (v.firstname ?? "").toString().trim(),
        lastname: (v.lastname ?? "").toString().trim(),
      };
    }
    if (typeof v === "string" && v.trim()) {
      // legacy: "Lastname, Firstname"
      const parts = v.split(",").map((p) => p.trim());
      const lastname = parts[0] || "";
      const firstname = parts[1] || "";
      return { id: "", firstname, lastname };
    }
    return { id: "", firstname: "", lastname: "" };
  };

  // Human-friendly label like "Lastname, Firstname" or "None"
  const ownerLabel = (o) => {
    const { firstname, lastname } = normalizeOwner(o);
    const label = [lastname, firstname].filter(Boolean).join(", ");
    return label || "None";
  };

  // Stable key so we can use objects with <Select> safely
  const ownerKey = (o) => {
    const { id, firstname, lastname } = normalizeOwner(o);
    return `${id}|${firstname}|${lastname}`;
  };

  // Helper account_owner is null
  const isEmptyOwner = (o) => {
    if (!o || typeof o !== "object") return true;
    const { id = "", firstname = "", lastname = "" } = o;
    return !(id || firstname || lastname);
  };

  // Combined options: fetched list + current value (if missing)
  const combinedOwnerOptions = React.useMemo(() => {
    const set = new Map(accountOwnerOptions.map(o => [ownerKey(o), normalizeOwner(o)]));
    const cur = normalizeOwner(client?.account_owner);
    if (!isEmptyOwner(cur)) {
      set.set(ownerKey(cur), cur);
    }
    return Array.from(set.values())
      .sort((a, b) =>
        `${a.lastname}, ${a.firstname}`.localeCompare(`${b.lastname}, ${b.firstname}`)
      );
  }, [accountOwnerOptions, client?.account_owner]);

  // Optional helper for the "None" menu key
  const NONE_KEY = ownerKey({ id: "", firstname: "", lastname: "" });

  // Utility function to clean up spaces (trim and remove multiple consecutive spaces)
  const cleanSpaces = (str) => {
    if (typeof str !== 'string') return str;
    return str.trim().replace(/\s+/g, ' ');
  };
  
  // Generate a log of what changed
  const generateChangeLog = (original, updated) => {
    const sections = {
      'CLIENT INFO': [],
      'JOB SITE ADDRESS': [],
      'CONTACT PERSON': [],
      'CLIENT CLASSIFICATION': [],
      'BILLING': []
    };
    
    console.log("=== CHANGE LOG COMPARISON ===");
    console.log("Original client:", original);
    console.log("Updated client:", updated);
    
    // Log all keys to see what fields are available
    console.log("Original keys:", Object.keys(original || {}));
    console.log("Updated keys:", Object.keys(updated || {}));
    
    // Helper to normalize date to timestamp (handles Date objects, strings, and null/undefined)
    const normalizeDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value.getTime();
      // If it's a string from JSON.stringify, convert to Date first
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.getTime();
    };
    
    // Helper to format date for display
    const formatDate = (value) => {
      if (!value) return 'N/A';
      if (value instanceof Date) return value.toLocaleDateString();
      const date = new Date(value);
      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
    };
    
    // CLIENT INFO - name and notes
    const clientInfoFields = ['name', 'notes'];
    clientInfoFields.forEach(field => {
      const origValue = original?.[field];
      const updValue = updated?.[field];
      if (!origValue && !updValue) return;
      if (origValue !== updValue) {
        sections['CLIENT INFO'].push(`${field}: "${origValue || 'N/A'}" → "${updValue || 'N/A'}"`);
      }
    });
    
    // CLIENT CLASSIFICATION - category, location_type, service_types, account_owner, parent_client, regular
    if (original?.category !== updated?.category) {
      sections['CLIENT CLASSIFICATION'].push(`category: "${original?.category || 'N/A'}" → "${updated?.category || 'N/A'}"`);
    }
    if (original?.location_type !== updated?.location_type) {
      sections['CLIENT CLASSIFICATION'].push(`location_type: "${original?.location_type || 'N/A'}" → "${updated?.location_type || 'N/A'}"`);
    }
    
    // const origRegular = original?.regular === true || original?.regular === 'true';
    // const updRegular = updated?.regular === true || updated?.regular === 'true';
    // if (origRegular !== updRegular) {
    //   sections['CLIENT CLASSIFICATION'].push(`regular: "${origRegular ? 'Yes' : 'No'}" → "${updRegular ? 'Yes' : 'No'}"`);
    // }

    // Helper to label client type
    const regularLabel = (value) => {
      if (value === true || value === 'true')   return 'Regular';
      if (value === false || value === 'false') return 'Non-Regular';
      return 'N/A';
    };

    // 🔹 use labels instead of booleans so null → false is detected
    const origRegularLabel = regularLabel(original?.regular);
    const updRegularLabel  = regularLabel(updated?.regular);
    if (origRegularLabel !== updRegularLabel) {
      sections['CLIENT CLASSIFICATION'].push(`Regular: "${origRegularLabel}" → "${updRegularLabel}"`);
    }

    // Work Permit Required (Yes/No)
    const ynLabel = (value) => (value === true || value === 'true') ? 'Yes' : 'No';
    const origWorkPermit = ynLabel(original?.work_permit_required);
    const updWorkPermit  = ynLabel(updated?.work_permit_required);
    if (origWorkPermit !== updWorkPermit) {
      sections['CLIENT CLASSIFICATION'].push(`Work Permit Required: "${origWorkPermit}" → "${updWorkPermit}"`);
    }
    
    const origTypes = (original.service_types || []).sort().join(', ');
    const updTypes = (updated.service_types || []).sort().join(', ');
    if (origTypes !== updTypes) {
      sections['CLIENT CLASSIFICATION'].push(`service_types: "${origTypes || 'N/A'}" → "${updTypes || 'N/A'}"`);
    }
    
    const origOwner = ownerLabel(original.account_owner);
    const updOwner = ownerLabel(updated.account_owner);
    if (origOwner !== updOwner) {
      sections['CLIENT CLASSIFICATION'].push(`account_owner: "${origOwner}" → "${updOwner}"`);
    }
    
    const origParent = original.parent_client?.name || 'None';
    const updParent = updated.parent_client?.name || 'None';
    if (origParent !== updParent) {
      sections['CLIENT CLASSIFICATION'].push(`parent_client: "${origParent}" → "${updParent}"`);
    }
    
    // BILLING - price, payment_terms, contract dates
    const billingFields = ['price', 'payment_terms', 'contract_period'];
    billingFields.forEach(field => {
      const origValue = original?.[field];
      const updValue = updated?.[field];
      if (!origValue && !updValue) return;
      if (origValue !== updValue) {
        sections['BILLING'].push(`${field}: "${origValue || 'N/A'}" → "${updValue || 'N/A'}"`);
      }
    });
    
    const origStartTime = normalizeDate(original.contract_start);
    const updStartTime = normalizeDate(updated.contract_start);
    if (origStartTime !== updStartTime) {
      const origStartStr = formatDate(original.contract_start);
      const updStartStr = formatDate(updated.contract_start);
      sections['BILLING'].push(`contract_start: "${origStartStr}" → "${updStartStr}"`);
    }
    
    const origEndTime = normalizeDate(original.contract_end);
    const updEndTime = normalizeDate(updated.contract_end);
    if (origEndTime !== updEndTime) {
      const origEndStr = formatDate(original.contract_end);
      const updEndStr = formatDate(updated.contract_end);
      sections['BILLING'].push(`contract_end: "${origEndStr}" → "${updEndStr}"`);
    }
    
    // JOB SITE ADDRESS
    const origAddresses = original.addresses || [];
    const updAddresses = updated.addresses || [];
    
    if (origAddresses.length !== updAddresses.length) {
      sections['JOB SITE ADDRESS'].push(`address_count: "${origAddresses.length}" → "${updAddresses.length}"`);
    }
    
    const maxAddressLen = Math.max(origAddresses.length, updAddresses.length);
    for (let i = 0; i < maxAddressLen; i++) {
      const origAddr = origAddresses[i] || {};
      const updAddr = updAddresses[i] || {};
      const addrFieldLabels = {
        house_unit_street: 'House/Unit/Street',
        barangay: 'Barangay',
        city: 'City',
        metro_province: 'Metro/Province',
        zipcode: 'Zipcode',
        area: 'Area (sqm)'
      };
      
      Object.keys(addrFieldLabels).forEach(field => {
        if ((origAddr[field] || '') !== (updAddr[field] || '')) {
          sections['JOB SITE ADDRESS'].push(`${addrFieldLabels[field]}: "${origAddr[field] || 'N/A'}" → "${updAddr[field] || 'N/A'}"`);
        }
      });
      
      if (origAddr.billing !== updAddr.billing) {
        sections['JOB SITE ADDRESS'].push(`Billing Address: "${origAddr.billing ? 'Yes' : 'No'}" → "${updAddr.billing ? 'Yes' : 'No'}"`);
      }
    }
    
    // CONTACT PERSON
    const origContacts = original.contacts || [];
    const updContacts = updated.contacts || [];
    
    if (origContacts.length !== updContacts.length) {
      sections['CONTACT PERSON'].push(`contact_count: "${origContacts.length}" → "${updContacts.length}"`);
    }
    
    const maxContactLen = Math.max(origContacts.length, updContacts.length);
    for (let i = 0; i < maxContactLen; i++) {
      const origContact = origContacts[i] || {};
      const updContact = updContacts[i] || {};
      
      if ((origContact.firstname || '') !== (updContact.firstname || '')) {
        sections['CONTACT PERSON'].push(`First Name: "${origContact.firstname || 'N/A'}" → "${updContact.firstname || 'N/A'}"`);
      }
      if ((origContact.lastname || '') !== (updContact.lastname || '')) {
        sections['CONTACT PERSON'].push(`Last Name: "${origContact.lastname || 'N/A'}" → "${updContact.lastname || 'N/A'}"`);
      }
      if (origContact.billing !== updContact.billing) {
        sections['CONTACT PERSON'].push(`Billing Contact: "${origContact.billing ? 'Yes' : 'No'}" → "${updContact.billing ? 'Yes' : 'No'}"`);
      }
      
      // Compare contact numbers
      const origNumbers = origContact.numbers || [];
      const updNumbers = updContact.numbers || [];
      if (JSON.stringify(origNumbers) !== JSON.stringify(updNumbers)) {
        const origNumStr = origNumbers.map(n => `${n.type}: ${n.number}`).join(', ') || 'N/A';
        const updNumStr = updNumbers.map(n => `${n.type}: ${n.number}`).join(', ') || 'N/A';
        sections['CONTACT PERSON'].push(`Contact Numbers: "${origNumStr}" → "${updNumStr}"`);
      }
      
      // Compare contact emails
      const origEmails = origContact.emails || [];
      const updEmails = updContact.emails || [];
      if (JSON.stringify(origEmails) !== JSON.stringify(updEmails)) {
        const origEmailStr = origEmails.map(e => e.email).join(', ') || 'N/A';
        const updEmailStr = updEmails.map(e => e.email).join(', ') || 'N/A';
        sections['CONTACT PERSON'].push(`Contact Emails: "${origEmailStr}" → "${updEmailStr}"`);
      }
    }
    
    // Build final output with section headers
    const output = [];
    Object.keys(sections).forEach(sectionName => {
      if (sections[sectionName].length > 0) {
        output.push(`**${sectionName}**`);
        output.push(...sections[sectionName]);
        output.push(''); // blank line between sections
      }
    });
    
    return output.join('\n').trim();
  };

  async function handleSubmit(e) {
    e.preventDefault();

    const ao = normalizeOwner(client.account_owner);
    // Clean up all string fields before validation and submission
    const cleanedClient = {
      ...client,
      name: cleanSpaces(client.name),
      addresses: client.addresses?.map(addr => ({
        ...addr,
        area: cleanSpaces(addr.area),
        house_unit_street: cleanSpaces(addr.house_unit_street),
        barangay: cleanSpaces(addr.barangay),
        city: cleanSpaces(addr.city),
        metro_province: cleanSpaces(addr.metro_province),
        zipcode: cleanSpaces(addr.zipcode),
      })) || [],
      contacts: client.contacts?.map(contact => ({
        ...contact,
        firstname: cleanSpaces(contact.firstname),
        lastname: cleanSpaces(contact.lastname),
        numbers: contact.numbers?.map(num => ({
          ...num,
          number: cleanSpaces(num.number),
        })) || [],
        emails: contact.emails?.map(email => ({
          ...email,
          email: cleanSpaces(email.email),
        })) || [],
      })) || [],
      account_owner: ao,
      price: cleanSpaces(client.price),
      payment_terms: cleanSpaces(client.payment_terms),
      notes: cleanSpaces(client.notes),
    };
  
    // ── 1) Date validations ──
    if ((cleanedClient.contract_start && !cleanedClient.contract_end) ||
        (!cleanedClient.contract_start && cleanedClient.contract_end)) {
      setErrorMessage("Please fill both Contract Start and End or leave both empty.");
      return;
    }
    if (cleanedClient.contract_start && cleanedClient.contract_end &&
        cleanedClient.contract_end <= cleanedClient.contract_start) {
      setErrorMessage("Contract End must be after Contract Start.");
      return;
    }
    setErrorMessage("");
  
    // Strip out metadata fields before sending
    const { timestamp_create, timestamp_update, ...updatedClientParameters } = cleanedClient;
  
    // ── 2) Client‐side validation ──
    try {
      await clientValidationSchema(null, clientsCache).validate(cleanedClient, {
        abortEarly: false  // collect *all* errors
      });
      setErrors({});            // clear old errors
    } catch (validationError) {
      // turn Yup’s list of errors into a path=>message map
      const fieldErrors = validationError.inner.reduce((acc, err) => {
        if (err.path) acc[err.path] = err.message;
        return acc;
      }, {});
      setErrors(fieldErrors);
      return;  // don’t proceed to API if validation failed
    }
  
    // ── 3) Update the client record ──
    setIsLoading(true);
    try {
      const response = await putClient(cleanedClient.id, updatedClientParameters);
      setSuccessMessage("Client updated successfully!");
      console.log("Client update response:", response);
  
      if (response) {
        // Update the form state with cleaned data
        setClient(cleanedClient);
        
        // ── 4) Post change logs ──
        const changeLog = generateChangeLog(originalClient, cleanedClient);
        console.log("Generated change log:", changeLog);
        console.log("Original client:", originalClient);
        console.log("Updated client:", cleanedClient);
        
        if (changeLog) {
          try {
            console.log("Posting client logs...");
            await postClientLogs(cleanedClient.id, changeLog, userUpdate);
            console.log("Client logs posted successfully");
          } catch (logError) {
            console.error("Error posting client logs:", logError);
            // Don't fail the whole update if logging fails
          }
        } else {
          console.log("No changes detected, skipping log post");
        }
        
        // Update original client for next comparison
        setOriginalClient(deepCopyClient(cleanedClient));
        setSuccessMessage("Client updated successfully!");
  
        // ── 5) If the name changed, update cache too ──
        if (hasNameChanged) {
          const cacheParams = { name: cleanedClient.name };
          const cacheResponse = await putCacheClient(cleanedClient.id, cacheParams);
          console.log("Cache update response:", cacheResponse);
          if (cacheResponse) {
            setSuccessMessage("Client and cache updated successfully!");
            setClientsCache(prev =>
              prev.map(cli =>
                cli.id === cleanedClient.id ? { ...cli, name: cleanedClient.name } : cli
              )
            );
            setSelectedClient(prev =>
              prev?.id === cleanedClient.id ? { ...prev, name: cleanedClient.name } : prev
            );
            setHasNameChanged(false);
          } else {
            setErrorMessage("An unexpected error occurred while updating the cache. Please try again later.");
          }
        }
  
        // 5) Only post notes if there’s non-blank content
        const rawNote = cleanedClient.notes || "";
        const trimmed = rawNote.trim();
        if (trimmed) {
          try {
            await postClientNotes(cleanedClient.id, trimmed, userUpdate);
            setSuccessMessage((prev) => prev + " Note saved.");
            // **Clear** the Notes field after success:
            setClient((prev) => ({ ...prev, notes: "" }));
          } catch (err) {
            console.error("Error posting client note:", err);
            setErrorMessage("Failed to save note: " + err.message);
          }
        }
  
      } else {
        setErrorMessage("An unexpected error occurred while updating the Client. Please try again later.");
      }
  
    } catch (apiError) {
      // If backend returns a structured { errors: { "<path>": "msg" } }
      if (apiError.response?.data?.errors) {
        setErrors(apiError.response.data.errors);
      } else {
        // fallback to a top‐level message
        setErrorMessage(apiError.message || "An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  
  // Contract status logic
  let contractStatusText = "Missing contract";
  let contractStatusColor = "default";
  if (client?.contract_start && client?.contract_end) {
    const today = new Date();
    const diffDays = (client.contract_end - today) / (1000 * 60 * 60 * 24);
    if (diffDays < 0) {
      contractStatusText = "Expired";
      contractStatusColor = "error";
    } else if (diffDays <= 60) {
      contractStatusText = "Will expire soon";
      contractStatusColor = "warning";
    } else {
      contractStatusText = "Active";
      contractStatusColor = "success";
    }
  };

  // ContractsTable & InvoiceTable functions

  const handleDeleteSuccess = (deletedContractId) => {
    setContracts(prev => prev.filter(c => (c.contract_id || c.id) !== deletedContractId));
  };



  return (
    <BaseWrapper
      titleIcon={<SupervisedUserCircleIcon />}
      title="Client Retention"
      subTitle="Manage Clients"
      subTitleRoute="/manage-clients"
      createButtonLabel="Create Client"
      onClickCreateButton={() => navigate("/create-client")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid item xs={12}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6} lg={6} xl={4}>
            <Autocomplete
              options={clientsCache}
              getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, params) => clientFilter(options, params)}
              openOnFocus                                                           
              autoHighlight
              value={selectedClient} // Controlled component
              onChange={(event, newValue) =>
                handleSearchClient(event, newValue)
              } // Updates selected value
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{display:'flex',flexDirection:'column'}}>
                    <span>{option.name}</span>
                    <small style={{opacity:.7}}>ID: {option.id}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Name"
                  variant="outlined"
                  fullWidth
                  error={!!error} // Displays error styling if there is an error
                  helperText={error || ""} // Displays error message
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            xl={6}
            container
            alignItems={"center"}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick}
              disabled={isLoading}
            >
              Search
            </Button>
            {isFormVisible && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewEditButtonClick}
                sx={{ ml: 2 }}
              >
                {isEdit ? "Lock Record" : "Edit Record"}{" "}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />
      {isFormVisible && client && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                    >
                      Basic Information
                    </Typography>
                  }
                />
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    padding: 3,
                    '&:last-child': {
                      paddingBottom: 3,
                    },
                  }}
                >
                  <Grid container spacing={3}>
                    {/* Client Name */}
                    <Grid item xs={12} md={6}>
                      <ClientName
                        clientName={client.name}
                        handleClientNameChange={handleClientNameChange}
                        loading={isLoading}
                        isEdit={isEdit}
                        errors={errors}
                      />
                      <TextField
                        label="Client ID"
                        value={client.id || ""}
                        fullWidth
                        size="small"
                        disabled
                        sx={{ mt: 2, width: "12ch" }}
                      />
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mt: 2, ml: 1 }}
                          onClick={() => navigate(`/job-order-history/${client.id}`)}
                          disabled={!client.id}
                        >
                          Show Job Order History
                        </Button>
                    </Grid>

                    {/* Notes card */}
                    <Grid item xs={12} md={6} sx={{ mb: 3 }}>
                      <Card
                        variant="outlined"
                        sx={{                          
                          display: 'flex',
                          flexDirection: 'column',
                          height: 'auto',
                        }}
                      >
                        <CardHeader
                          title={<Typography variant="subtitle1">Notes</Typography>}
                        />
                        <CardContent>
                          <TextField
                            placeholder="ADD NOTE"
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            value={client.notes || ''}
                            onChange={e =>
                              setClient(prev => ({ ...prev, notes: e.target.value }))
                            }
                            disabled={!isEdit}
                          />
                        </CardContent>

                        {/* toggle + expand buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pr: 2 }}>
                          <IconButton
                            size="small"
                            onClick={() => setHideHistory(h => !h)}
                            title={hideHistory ? "Show history" : "Hide history"}
                          >
                            {hideHistory ? <VisibilityIcon fontSize="small"/> : <VisibilityOffIcon fontSize="small"/>}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setDialogOpen(true)}
                            title="Open in larger view"
                          >
                            <OpenInFullIcon fontSize="small"/>
                          </IconButton>
                        </Box>

                        {/* scrollable history (hidden if hideHistory=true) */}
                        {!hideHistory && clientNotes.length > 0 && (
                          <CardContent sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {clientNotes.map(note => (
                              <Card key={note.id} variant="outlined" sx={{ mb: 2, p: 1, backgroundColor: '#fafafa' }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                  {new Date(note.timestamp_update).toLocaleString()} by {note.user_update}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    mt: 1,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {note.client_notes}
                                </Typography>
                              </Card>
                            ))}
                          </CardContent>
                        )}
                      </Card>

                      {/* full-screen dialog */}
                      <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        fullWidth
                        maxWidth="md"
                      >
                        <DialogTitle>Notes History</DialogTitle>
                        <DialogContent dividers>
                          {clientNotes.map(note => (
                            <Card key={note.id} variant="outlined" sx={{ mb: 2, p: 1 }}>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {new Date(note.timestamp_update).toLocaleString()} by {note.user_update}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  mt: 1,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {note.client_notes}
                              </Typography>
                            </Card>
                          ))}
                        </DialogContent>
                      </Dialog>

                      {/* </Card> */}
                    </Grid>
                  </Grid>
                  <StyledHr />
                  {/* Job Site Address */}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <ClientAddress
                        clientAddresses={client.addresses}
                        nationalAddresses={nationalAddresses}
                        handleAddressChange={handleAddressChange}
                        handleCityChange={handleCityChange}
                        handleBarangayChange={handleBarangayChange}
                        handleAddressCheckboxChange={handleAddressCheckboxChange}
                        handleAddAddress={handleAddAddress}
                        handleRemoveAddress={handleRemoveAddress}
                        loading={isLoading}
                        isEdit={isEdit}
                        errors={errors}
                      />
                    </Grid>
                  </Grid>

                  {/* Work Permit Required */}
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" size="small">
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem", color: "gray", mb: 0.5 }}
                        >
                          Work Permit Required?
                        </Typography>
                        <RadioGroup
                          row
                          value={String(!!client.work_permit_required)}
                          onChange={(e) => handleWorkPermitRequiredChange(e.target.value)}
                        >
                          <FormControlLabel
                            value="true"
                            control={<Radio size="small" />}
                            label="Yes"
                            sx={{ mr: 3, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                            disabled={!isEdit}
                          />
                          <FormControlLabel
                            value="false"
                            control={<Radio size="small" />}
                            label="No"
                            sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                            disabled={!isEdit}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <StyledHr />
                  {/* Contact Person Section */}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <ClientContact
                        clientContacts={client.contacts}
                        handleContactChange={handleContactChange}
                        handleAddContact={handleAddContact}
                        handleRemoveContact={handleRemoveContact}
                        handleContactCheckboxChange={
                          handleContactCheckboxChange
                        }
                        handleContactDetailsChange={handleContactDetailsChange}
                        handleAddNumber={handleAddNumber}
                        handleRemoveNumber={handleRemoveNumber}
                        handleAddEmail={handleAddEmail}
                        handleRemoveEmail={handleRemoveEmail}
                        loading={isLoading}
                        isEdit={isEdit}
                        errors={errors}
                      />
                    </Grid>
                  </Grid>
                  
                  <StyledHr />

                  <Grid container sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <CardHeader
                        title={
                          <Typography
                            variant="h6"
                            sx={{
                              paddingBottom: "0.5rem",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              fontSize: "1rem",
                            }}
                          >
                            Client Classifications
                          </Typography>
                        }
                        sx={{ pb: 0, pt: 0 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="category-label">
                              Category
                            </InputLabel>
                            <Select
                              labelId="category-label"
                              value={client.category}
                              onChange={(e) =>
                                handleCategoryChange(e.target.value)
                              }
                              label="Category"
                              disabled={!isEdit}
                            >
                              <MenuItem
                                value="VVIP"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                VVIP
                              </MenuItem>
                              <MenuItem
                                value="VIP"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                VIP
                              </MenuItem>
                              <MenuItem
                                value="Gold"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                Gold
                              </MenuItem>
                              <MenuItem
                                value="Silver"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                Silver
                              </MenuItem>
                              <MenuItem
                                value="Bronze"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                Bronze
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={8} lg={6} xl={6}>
                          <Autocomplete
                            options={clientsCache}
                            getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            filterOptions={(options, params) => clientFilter(options, params)}   // ⟵ nicer matching
                            openOnFocus                                                           
                            autoHighlight
                            value={selectedParentClient} // Controlled component
                            onChange={(event, newValue) =>
                              handleSelectedParentClient(event, newValue)
                            } // Updates selected value
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                <div style={{display:'flex',flexDirection:'column'}}>
                                  <span>{option.name}</span>
                                  <small style={{opacity:.7}}>ID: {option.id}</small>
                                </div>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Parent Client"
                                variant="outlined"
                                fullWidth
                                error={!!error} // Displays error styling if there is an error
                                helperText={error || ""} // Displays error message
                                size="small"
                              />
                            )}
                            disabled={!isEdit}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl component="fieldset" sx={{ width: '100%' }}>
                            <Typography
                              variant="body2" // Smaller text variant
                              sx={{
                                paddingBottom: "0.5rem", // Adjust spacing as needed
                                fontWeight: "normal", // Remove bold for label-like appearance
                                textTransform: "none", // Keep natural casing if preferred
                                fontSize: "0.875rem", // Smaller font size for a label
                                color: "gray", // Change the text color to gray
                              }}
                            >
                              Service Type
                            </Typography>
                            <Grid container spacing={1}>
                              {competencies.map((option) => {
                                const formattedOption = option
                                  .toLowerCase()
                                  .replace(/\s+/g, "_");
                                return (
                                  <Grid item xs={12} sm={6} md={4} lg={3} key={formattedOption}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          name={formattedOption}
                                          checked={
                                            Array.isArray(client?.service_types) &&
                                            client.service_types.includes(
                                              formattedOption
                                            )
                                          }
                                          onChange={(e) =>
                                            handleServiceTypeChange(
                                              e,
                                              formattedOption
                                            )
                                          }
                                        />
                                      }
                                      label={option}
                                      disabled={!isEdit}
                                      sx={{
                                        margin: 0, // Remove default margins
                                        width: '100%', // Take full width of grid item
                                        alignItems: "center", // Align items properly
                                        "& .MuiTypography-root": {
                                          fontSize: "0.875rem", // Smaller label text size
                                        },
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>                          
                          <FormControl fullWidth size="small">
                            <InputLabel id="location-type-label">Location Type</InputLabel>
                            <Select
                              labelId="location-type-label"
                              value={client.location_type}
                              onChange={e => handleLocationTypeChange(e.target.value)}
                              label="Location Type"
                              size="small"
                              disabled={!isEdit}
                            >
                              {allLocationTypes.map(type => (
                                <MenuItem key={type} value={type} sx={{ fontSize: "0.875rem" }}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {/* Account Owner */}
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="account-owner-label">Account Owner</InputLabel>
                            <Select
                              labelId="account-owner-label"
                              value={ownerKey(client.account_owner)}
                              onChange={(e) => {
                                const valueKey = e.target.value;
                                if (valueKey === NONE_KEY) {
                                  handleAccountOwnerChange({ id: "", firstname: "", lastname: "" });
                                  return;
                                }
                                const found = combinedOwnerOptions.find((o) => ownerKey(o) === valueKey);
                                handleAccountOwnerChange(normalizeOwner(found));
                              }}
                              label="Account Owner"
                              size="small"
                              disabled={!isEdit}
                              renderValue={() => ownerLabel(client.account_owner)}
                            >
                              <MenuItem value={NONE_KEY}><em>None</em></MenuItem>
                              {combinedOwnerOptions.map((o) => (
                                <MenuItem key={ownerKey(o)} value={ownerKey(o)} sx={{ fontSize: "0.875rem" }}>
                                  {ownerLabel(o)}
                                </MenuItem>
                              ))}
                            </Select>
                            {isLoadingOwners && <FormHelperText>Loading owners…</FormHelperText>}
                          </FormControl>
                        </Grid>
                        {/* Client Type (Regular / Non-Regular) */}
                        <Grid item xs={12}>
                          <FormControl component="fieldset" size="small">
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.875rem", color: "gray", mb: 0.5 }}
                            >
                              Client Type
                            </Typography>
                            <RadioGroup
                              row
                              value={String(!!client.regular)}
                              onChange={handleRegularChange}
                            >
                              <FormControlLabel
                                value="true"
                                control={<Radio size="small" />}
                                label="Regular"
                                sx={{ mr: 3, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                                disabled={!isEdit}
                              />
                              <FormControlLabel
                                value="false"
                                control={<Radio size="small" />}
                                label="Non-Regular"
                                sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                                disabled={!isEdit}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>                     
                      </Grid>                      
                    </Grid>
                  </Grid>

                  <StyledHr />
                  <Accordion
                    disableGutters
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderRadius: 1,
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="legacy-billing-content"
                      id="legacy-billing-header"
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          fontSize: "1rem",
                        }}
                      >
                        Billing (Legacy)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      {/* Legacy billing fields */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Price"
                            fullWidth
                            value={client.price}
                            onChange={(e) =>
                              handlePriceChange(e.target.value)
                            }
                            size="small"
                            disabled={!isEdit}
                            error={!!errors.price}
                            helperText={errors.price}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Payment Terms"
                            fullWidth
                            value={client.payment_terms}
                            onChange={(e) =>
                              handlePaymentTermsChange(e.target.value)
                            }
                            size="small"
                            disabled={!isEdit}
                            error={!!errors.payment_terms}
                            helperText={errors.payment_terms}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6} md={4}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Contract Start"
                              value={client.contract_start}
                              onChange={handleContractStartChange}
                              renderInput={params => <TextField {...params} size="small" fullWidth error={!!errors.contract_start} helperText={errors.contract_start} />}
                              disabled={!isEdit}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Contract End"
                              value={client.contract_end}
                              onChange={handleContractEndChange}
                              renderInput={params => <TextField {...params} size="small" fullWidth error={!!errors.contract_end} helperText={errors.contract_end} />}
                              disabled={!isEdit}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'stretch', sm: 'flex-start', md: 'center' },
                          }}
                        >
                          <Chip
                            label={contractStatusText}
                            color={contractStatusColor}
                            sx={{ 
                              fontWeight: 'bold', 
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              width: { xs: '100%', sm: 'auto' },
                              maxWidth: '100%',
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} md={8} lg={6}>
                          <TextField 
                            label="Contract Period" 
                            fullWidth size="small" 
                            value={client.contract_period} 
                            disabled 
                            helperText="Viewing only: Please use Contract Start and Contract End to record the client's contract period. This field will be removed soon." 
                            />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                  <ClientLogs clientId={client.id} />
                </Card>
              </Grid>

              {isEdit && (
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                  sx={{ px: { xs: 2, sm: 0 } }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => window.location.reload()}
                    sx={{
                      fontSize: "0.875rem",
                      color: "#FF4C4C",
                      borderColor: "#FF4C4C",
                    }}
                    disabled={isLoading} // Optionally disable the button while loading
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{
                      fontSize: "0.875rem",
                    }}
                    disabled={isLoading} // Optionally disable the button while loading
                  >
                    {isLoading ? "Submitting..." : "Submit"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>

          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <CardHeader
              title={<Typography variant="h6">Contracts &amp; Invoices</Typography>}
              sx={{ px: 0, pt: 0 }}
            />
            <CardContent sx={{ px: 0, pb: "0 !important" }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ContractsTable
                    source="manageClients"
                    selectedClient={selectedClient}
                    contracts={contracts}
                    loading={contractsLoading}
                    onDeleteSuccess={handleDeleteSuccess}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InvoicesTable
                    source="manageClients"
                    selectedClient={selectedClient}
                    invoices={invoices}
                    loading={invoicesLoading}
                    hasSearched={true}
                    onDeleteSuccess={(deletedId) => setInvoices(prev => prev.filter(inv => inv.invoiced_id !== deletedId))}
                    onInvoicesFetch={(fetched) => setInvoices(fetched)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </BaseWrapper>
  );
}

export default ManageClients;
